<template>
	<div class="task-list-cell-planned-time" @click.stop>
		<div
			class="task-list-cell-planned-time__shell"
			:class="{
				'task-list-cell-planned-time__shell--editing': isEditing,
			}"
		>
			<button
				v-if="!isEditing"
				type="button"
				class="task-list-cell-planned-time__trigger"
				:aria-label="$t('task_progress_estimated')"
				@click="startEditing"
			>
				<span class="task-list-cell-planned-time__value">
					{{ displayTriggerText }}
				</span>
			</button>
			<SuiTimeInput
				v-else
				ref="$timeInput"
				v-model="timeValue"
				class="task-list-cell-planned-time__time-input"
				:size="SuiInputSize.SMALL"
				:align="SuiInputAlign.Right"
				full-width
				hide-clear-button
				:hours-per-day="hoursPerDay"
				@submit="commitEdit"
				@blur="commitEdit"
				@keydown.esc.prevent="clearEditing"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import {
	SuiTimeInput,
	SuiInputAlign,
	SuiInputSize,
} from '@developers/scoro-ui';
import {
	useTaskListTaskSaveMutation,
	type Task,
	type TaskListTaskFragment,
} from '@/generated/graphql';
import { useSettings, useOptimisticSave } from '@/composables';
import { Setting } from '@/constants/settings';
import { DEFAULT_TIME_ENTRY_VALUE } from '@/features/TimeEntry/constants';
import { shortDurationValue } from '@/utils/formatting';
import { timeToSeconds } from '@/utils/time';

export type TaskListCellPlannedTimeProps = {
	taskId: Task['id'];
	totalDurationPlanned: Task['totalDurationPlanned'];
};

const { taskId, totalDurationPlanned } =
	defineProps<TaskListCellPlannedTimeProps>();

const emit = defineEmits<{
	'task-updated': [task?: TaskListTaskFragment];
}>();

const { setting } = useSettings();
const { mutate } = useTaskListTaskSaveMutation();

const DEFAULT_HOURS_PER_DAY = 8;

const hoursPerDay = computed(() => {
	const raw = Number(setting(Setting.SYSTEM_DAY_UNIT_SUB_VALUE));

	if (Number.isFinite(raw) && raw > 0) {
		return raw;
	}

	return DEFAULT_HOURS_PER_DAY;
});

const { committedValue: committedSeconds, save } = useOptimisticSave(
	() => totalDurationPlanned
);
const isEditing = ref(false);
const timeValue = ref('');
const $timeInput = ref<InstanceType<typeof SuiTimeInput> | null>(null);

const getPlannedTimeNativeInput = (): HTMLInputElement | null =>
	($timeInput.value?.$el?.querySelector(
		'input'
	) as HTMLInputElement | null) ?? null;

const displayTriggerText = computed(() => {
	if (!committedSeconds.value) {
		return '';
	}

	return shortDurationValue(committedSeconds.value);
});

const formatDraftForInput = (seconds: number) => {
	if (!seconds) {
		return DEFAULT_TIME_ENTRY_VALUE;
	}

	return shortDurationValue(seconds);
};

const startEditing = async () => {
	timeValue.value = formatDraftForInput(committedSeconds.value);
	isEditing.value = true;
	await nextTick();
	const input = getPlannedTimeNativeInput();
	if (!input) {
		return;
	}

	input.focus();
	queueMicrotask(() => {
		input.select();
	});
};

const clearEditing = () => {
	isEditing.value = false;
	timeValue.value = '';
};

const commitEdit = async () => {
	if (!isEditing.value) {
		return;
	}

	const parsed = timeToSeconds(timeValue.value);
	clearEditing();

	if (parsed === committedSeconds.value) {
		return;
	}

	await save(parsed, async () => {
		const result = await mutate({
			input: {
				taskId,
				durationPlanned: parsed,
			},
		});

		if (result?.data?.TaskSave) {
			emit('task-updated', result.data.TaskSave);
			return result.data.TaskSave.totalDurationPlanned;
		}
	});
};
</script>

<style scoped lang="scss">
.task-list-cell-planned-time {
	width: 100%;

	&__shell {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		width: calc(100% + 2rem);
		min-height: 4rem;
		padding: 0 1rem;
		margin: -1rem;
		border-radius: 0.5rem;

		&:hover:not(#{&}--editing) {
			background-color: $neutral-20;
		}

		&--editing {
			padding: 0;
			background-color: $neutral-20;
		}
	}

	&__trigger {
		display: flex;
		width: 100%;
		align-self: stretch;
		align-items: center;
		justify-content: flex-end;
		margin: 0;
		padding: 0;
		border: none;
		background: transparent;
		cursor: pointer;
		font: inherit;
		color: inherit;
	}

	&__value {
		font-feature-settings: 'tnum';
	}

	&__time-input {
		flex: 1 1 auto;
		width: 100%;
		min-width: 0;
	}
}
</style>
